import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const provider = sp.get("provider");
  const paypalToken = sp.get("token"); // PayPal appends ?token=ORDER_ID on return_url
  const [status, setStatus] = useState("checking");
  const [detail, setDetail] = useState(null);
  const { refresh } = useAuth();
  const attemptsRef = useRef(0);
  const processedRef = useRef(false);

  // PayPal capture flow
  useEffect(() => {
    if (provider !== "paypal" || !paypalToken || processedRef.current) return;
    processedRef.current = true;
    (async () => {
      try {
        const { data } = await api.post(`/payments/paypal/capture/${paypalToken}`);
        setDetail({ package_id: data.package_id, status: data.status });
        if (data.status === "COMPLETED" || data.status === "already_captured") {
          setStatus("paid");
          refresh();
        } else {
          setStatus("error");
        }
      } catch {
        setStatus("error");
      }
    })();
  }, [provider, paypalToken, refresh]);

  // Stripe polling flow (unchanged)
  useEffect(() => {
    if (provider === "paypal") return;
    if (!sessionId) { setStatus("error"); return; }
    let stopped = false;

    const poll = async () => {
      if (stopped) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setDetail(data);
        if (data.payment_status === "paid") { setStatus("paid"); refresh(); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (attemptsRef.current > 10) { setStatus("timeout"); return; }
        setTimeout(poll, 2000);
      } catch {
        if (attemptsRef.current > 10) setStatus("error");
        else setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId, provider, refresh]);

  return (
    <div className="container-x py-24 max-w-xl text-center">
      {status === "checking" && (
        <>
          <Loader2 className="w-16 h-16 mx-auto text-[#FF5E00] animate-spin mb-6" />
          <h1 className="section-heading mb-4">Verifying...</h1>
          <p className="text-white/60">Confirming your payment with {provider === "paypal" ? "PayPal" : "Stripe"}.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="w-16 h-16 mx-auto text-[#B3FF00] mb-6" />
          <h1 className="section-heading mb-4">Payment Completed!</h1>
          <p className="text-white/70 mb-8">
            Thank you for purchasing the <b>{detail?.package_id}</b> plan. Your credits have been added.
          </p>
          <div className="flex gap-3 justify-center">
            <Link to="/profile" className="btn-primary" data-testid="payment-goto-profile">Go to Profile</Link>
            <Link to="/catalog" className="btn-outline">Unlock tracks</Link>
          </div>
        </>
      )}
      {(status === "expired" || status === "error" || status === "timeout") && (
        <>
          <XCircle className="w-16 h-16 mx-auto text-red-400 mb-6" />
          <h1 className="section-heading mb-4">Payment not completed</h1>
          <p className="text-white/70 mb-8">Something went wrong. Try again from your chosen plan.</p>
          <Link to="/pricing" className="btn-primary">Back to Plans</Link>
        </>
      )}
      <div className="h-32" />
    </div>
  );
}
